import React from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { OrderForm } from './components/OrderForm';
import { ReviewForm } from './components/ReviewForm';
import { motion } from 'framer-motion';
import { Star, Heart, Truck, Shield, Leaf } from 'lucide-react';

// Features Section Component
const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: Leaf,
      title: '100% Natural',
      description: 'Made from fresh, high-quality ginger and garlic without any preservatives or additives.',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: Heart,
      title: 'Made with Love',
      description: 'Each batch is carefully prepared with traditional methods and lots of care.',
      color: 'from-red-400 to-red-600'
    },
    {
      icon: Shield,
      title: 'Quality Assured',
      description: 'We maintain the highest standards of hygiene and quality in every preparation.',
      color: 'from-blue-400 to-blue-600'
    },
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Fresh paste delivered to your doorstep quickly while maintaining perfect quality.',
      color: 'from-purple-400 to-purple-600'
    }
  ];

  return (
    <section id="features" className="py-20 px-4 bg-white/50 backdrop-blur-sm">
      <div className="container mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">Why Choose Us?</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We're committed to delivering the finest homemade ginger garlic paste with exceptional quality and taste.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="card p-8 text-center group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <motion.div
                className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300`}
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <feature.icon className="w-10 h-10 text-white" />
              </motion.div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section Component
const TestimonialsSection: React.FC = () => {
  const testimonials = [
    {
      name: 'Srinivas',
      role: 'Shop owner',
      content: 'The best ginger garlic paste I\'ve ever used! So fresh and flavorful. It has completely transformed my cooking.',
      rating: 5
    },
    {
      name: 'Raju',
      role: 'Shop owner',
      content: 'Consistent quality and amazing taste. Our customers love the dishes made with this paste. Highly recommended!',
      rating: 5
    },
    {
      name: 'Naveen',
      role: 'Shop owner',
      content: 'Perfect balance of ginger and garlic. You can really taste the freshness. A game-changer for home cooking!',
      rating: 5
    }
  ];

  return (
    <section className="py-20 px-4">
      <div className="container mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">What Our Customers Say</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Don't just take our word for it - hear from our satisfied customers who love our homemade paste.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              className="card p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
              <div>
                <p className="font-semibold text-gray-800">{testimonial.name}</p>
                <p className="text-sm text-gray-500">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

function App() {
  return (
    <div className="min-h-screen">
      <Header />
      <Hero />
      
      {/* Features Section */}
      <FeaturesSection />
      
      {/* Order Section */}
      <section id="order" className="py-20 px-4 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">Ready to Order?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Place your order now and enjoy the fresh, authentic taste of homemade ginger garlic paste delivered to your doorstep.
            </p>
          </motion.div>
          <OrderForm />
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Customer Reviews Section */}
      <section id="reviews" className="py-20 px-4 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">Share Your Experience</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your feedback matters! Help us improve by sharing your experience with our homemade ginger garlic paste.
            </p>
          </motion.div>
          <ReviewForm />
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="bg-gradient-to-r from-primary to-gray-800 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 gradient-text">Sai Ginger Garlic Paste</h3>
              <p className="text-gray-300 leading-relaxed">
                Premium homemade ginger garlic paste, crafted with love and delivered fresh to your kitchen.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-300 hover:text-white transition-colors">Home</a></li>
                <li><a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a></li>
                <li><a href="#order" className="text-gray-300 hover:text-white transition-colors">Order Now</a></li>
                <li><a href="#reviews" className="text-gray-300 hover:text-white transition-colors">Reviews</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-4">Contact Info</h4>
              <p className="text-gray-300">📞 9381956301</p>
              <p className="text-gray-300">📧 vishwasai2002@gmail.com</p>
              <p className="text-gray-300">� Deshaipet, Near CKM College, Warangal</p>
              <p className="text-gray-300">🏠 Homemade with love</p>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400 mb-2">© 2024 Sai Ginger Garlic Paste. All rights reserved.</p>
            <p className="text-sm text-gray-500 flex items-center justify-center">
              Made with <Heart className="w-4 h-4 mx-1 text-red-500" /> for fresh, authentic flavors
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
